import { useState } from 'react';
import { Link } from 'react-router-dom';
import { forgotPassword } from '../../shared/api/auth';
import { ApiError } from '../../shared/api/client';
import { ALLOWED_INVITE_DOMAINS, isAllowedEmail } from '@shared/contracts/invite';

/**
 * Forgot-password page. Submits an email to /auth/forgot, which is
 * enumeration-proof on the server - so the success message is identical
 * whether or not the email matched a real account. The user takes the
 * follow-up action via the inbox, not the UI.
 */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const emailOk = email.length > 0 && isAllowedEmail(email);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!emailOk) return;
    setError(null);
    setBusy(true);
    try {
      await forgotPassword(email.trim().toLowerCase());
      setSubmitted(true);
    } catch (err) {
      if (err instanceof ApiError) setError(err.body.message);
      else setError('Could not send the reset email. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell title="Forgot password" subtitle="Reset your password">
      {submitted ? (
        <div className="space-y-4">
          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-3 text-sm text-emerald-200">
            If an account exists for <span className="font-medium">{email}</span>,
            we&apos;ve sent a password-reset link. It&apos;s valid for 30 minutes.
            Check your inbox (and spam).
          </div>
          <Link to="/login" className="btn-primary w-full inline-flex justify-center">
            Back to sign in
          </Link>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <p className="text-xs text-slate-400">
            Enter your{' '}
            <span className="text-accent-300/90 font-medium">
              {ALLOWED_INVITE_DOMAINS.join(', ')}
            </span>{' '}
            email and we&apos;ll send you a reset link.
          </p>

          <label className="block">
            <div className="label mb-1">Email</div>
            <input
              type="email"
              autoComplete="username"
              className="input"
              placeholder="name@tricorian.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            {email.length > 0 && !emailOk && (
              <p className="text-[11px] text-rose-400 mt-1">
                Email must end in @{ALLOWED_INVITE_DOMAINS[0]}.
              </p>
            )}
          </label>

          {error && (
            <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="btn-primary w-full"
            disabled={!emailOk || busy}
          >
            {busy ? 'Sending…' : 'Send reset link'}
          </button>

          <p className="mt-3 text-xs text-slate-400 text-center">
            Remembered it?{' '}
            <Link to="/login" className="link-brand">
              Back to sign in
            </Link>
          </p>
        </form>
      )}
    </AuthShell>
  );
}

function AuthShell(props: {
  title: string;
  subtitle: string;
  children: React.ReactNode;
}) {
  return (
    <div
      className="relative min-h-full w-full flex items-center justify-center p-6"
      style={{
        background:
          'linear-gradient(135deg, #0f172a 0%, #11233f 55%, #0e2a3a 100%)',
      }}
    >
      <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-slate-900 p-6 shadow-soft-lg">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-10 w-10 rounded-lg bg-gradient-brand text-white flex items-center justify-center font-semibold">
            T
          </div>
          <div>
            <div className="text-base font-semibold text-slate-100">{props.title}</div>
            <div className="text-[11px] uppercase tracking-[0.16em] text-accent-300/80">
              {props.subtitle}
            </div>
          </div>
        </div>
        {props.children}
      </div>
    </div>
  );
}
